import {
  styled,
  Table,
  TableCell,
  tableCellClasses,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import CustomTableBody from "../../utilis/component/CustomTableBody";
import moment from "moment";

// Utility function to access nested object values
// function getNestedValue(obj: any, path: string, defaultValue: any = undefined): any {
//   if (obj === null || typeof obj !== "object") {
//     return defaultValue;
//   }
//   const segments = path.split(/[\.\[\]]+/).filter(segment => segment !== "");
//   let current = obj;
//   for (const segment of segments) {
//     if (current === null || typeof current !== "object" || !(segment in current)) {
//       return defaultValue;
//     }
//     current = current[segment];
//   }
//   return current;
// }

function getNestedValue(obj: any, key: string) {
  const splited = key.split(".");
  if (splited.length > 1) {
    return splited.reduce((acc: any, curr: string) => {
      if (acc && typeof acc === "object" && curr in acc) {
        return acc[curr];
      }
      return undefined;
    }, obj);
  } else {
    return obj[key];
  }
}

function resolveValue(value: any) {
  if (value == null) return "";

  if (typeof value !== "object") return String(value ?? "").replace(/#\$/g, " ");

  if ("value" in value) return value.value;

  if ("name" in value) return value.name;

  return JSON.stringify(value);
}

export type TablePropType = { heading: string; key: string; date?: boolean | string; format?: string, numberType?: "round" | "full" }[]

const TableComponent: FC<{
  CustomHeader?: ReactNode;
  align?: "left" | "center" | "right";
  data: any[] | null | undefined; // Updated type to allow null/undefined
  tableProps: TablePropType;
  loading?: boolean;
  pagination?: "frontend" | "backend";
  paginationChange?: (props: { page: number; pageSize: number }) => void;
  totalCount?: number;
  showPaginationInside?: boolean;
  externalPage?: number;
  externalRowsPerPage?: number;
  cellPadding?: string;
  compactHeader?: boolean
  alertMessage?: string | null
}> = ({
  CustomHeader,
  align = "left",
  data,
  tableProps,
  loading,
  pagination = "frontend",
  paginationChange,
  totalCount,
  showPaginationInside = true,
  externalPage,
  externalRowsPerPage,
  compactHeader = false,
  alertMessage = null,
}) => {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontSize: compactHeader ? 10 : 12,
      fontWeight: 600,
      padding: compactHeader ? "2px 6px" : "4px 8px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 20,
      padding: "4px 178px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: 1.6, //
    },
  }));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    if (paginationChange && pagination === "backend") {
      paginationChange({ page: newPage, pageSize: rowsPerPage });
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const pageSize = parseInt(event.target.value, 10);
    if (paginationChange && pagination === "backend") {
      paginationChange({ page, pageSize });
    }
    setRowsPerPage(pageSize);
    setPage(0);
  };

  const tabledata = useMemo(() => {
    // Ensure data is an array; fallback to empty array if not
    const validData = Array.isArray(data) ? data : [];
    if (pagination === "frontend") {
      return validData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
    } else {
      return validData;
    }
  }, [data, page, rowsPerPage]);

  useEffect(() => {
    if (pagination !== "backend") {
      setPage(0);
      setRowsPerPage(25);
    }
  }, [data]);

  const effectivePage = externalPage ?? page;
  const effectiveRowsPerPage = externalRowsPerPage ?? rowsPerPage;

  return (
    <Table
      size="small"
      stickyHeader
      sx={{
        minWidth: 200, // reduced from 500
        tableLayout: "auto", // columns auto-resize to fit content
      }}
      aria-label="custom pagination table"
    >
      {CustomHeader ? (
        CustomHeader
      ) : (
        <TableHead>
          <TableRow>
            <StyledTableCell>SL NO</StyledTableCell>
            {tableProps.map((tableProp, i) => (
              <StyledTableCell key={`customReportHead${i}`}>
                {tableProp.heading}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
      )}
      <CustomTableBody
        alert={alertMessage || "No data found"}
        dataLoading={Boolean(loading)}
        isData={Boolean(Array.isArray(data) && data.length > 0)} // Updated isData check
        totalSpan={{ col: tableProps.length + 1, row: rowsPerPage }}
      >
        {tabledata.map((row, i) => (
          <TableRow key={`customReportRow${i}`}>
            <TableCell>
              {effectivePage * effectiveRowsPerPage + (i + 1)}
            </TableCell>
            {tableProps.map((tableProp, j) => (
              <TableCell align={align} key={`customReportRow${i}Col${j}`}>
                {tableProp.key != "" &&
                  (() => {
                    const value = getNestedValue(row, tableProp.key);
                    if (value || value === 0) {
                      if (tableProp.date) {
                        return moment(
                          value,
                          tableProp.format ? tableProp.format : "YYYY-MM-DD",
                        ).format(
                          typeof tableProp.date === "string"
                            ? tableProp.date
                            : "DD-MM-YYYY",
                        );
                      }
                      if (Boolean(tableProp.numberType)) {
                        return tableProp.numberType == "round"
                          ? String(Number(value).toFixed(2))
                          : value;
                      }
                      return resolveValue(value);
                    }
                    return "";
                  })()}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </CustomTableBody>
      {showPaginationInside && (
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20]}
              colSpan={tableProps.length + 1}
              count={totalCount || (Array.isArray(data) ? data.length : 0)}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: { "aria-label": "rows per page" },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
};

export default TableComponent;
